import * as React from 'react';
// Import props.
import {
	ComparativeInsuredInformationSummaryProps,
	FormInsuredInformationComparisonProps,
	FormProps,
	ScrollbarProps,
} from '@app/props';

interface UseFormInsuredInformationComparisonParameters {
	id: string;
	props: FormInsuredInformationComparisonProps,
}

interface UseFormInsuredInformationComparisonResults {
	applyFormProps: () => FormProps & ScrollbarProps;
	applyComparativeInsuredInformationSummaryProps: () => ComparativeInsuredInformationSummaryProps;
}

const useFormInsuredInformationComparison = (params: UseFormInsuredInformationComparisonParameters): UseFormInsuredInformationComparisonResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
		};
	};

	/**
	 * Returns props for the comparative insured information summary. 
	 **/
	const applyComparativeInsuredInformationSummaryProps = (): ComparativeInsuredInformationSummaryProps => {
		return {
			id: generateIdForComparativeInsuredInformationSummary(),
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			comparisonInsuredInfoSummary: params.props.comparisonInsuredInfoSummary,
			onEditInsuredInformation: params.props.onEditInsuredInformation,
			possibleStates: params.props.possibleStates,
			focusToErrorOnMount: doesInsuredInformationRequireReview,
		};
	};

	// #endregion exported methods


	// #region private methods

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the comparative insured information summary. 
	 **/
	const generateIdForComparativeInsuredInformationSummary = (): string => {
		return `${generateIdForForm()}-insuredInformationSummary`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const doesInsuredInformationRequireReview = React.useMemo(() => {
		return params.props.comparisonInsuredInfoSummary.requiresReview
	}, [params.props.comparisonInsuredInfoSummary.requiresReview]);

	// #endregion variables

	return {
		applyFormProps,
		applyComparativeInsuredInformationSummaryProps,
	};
}

// Export parameters.
export {
	UseFormInsuredInformationComparisonParameters,
}

// Export results.
export {
	UseFormInsuredInformationComparisonResults,
}

export default useFormInsuredInformationComparison;